import * as S from './styles'
import { useState, useEffect, useMemo } from 'react'
import axios from 'axios'
import { LoadingPage } from '../LoadingPage'
import { ErrorPage } from '../ErrorPage'
import FormField from '../../components/FormField'
import * as G from '../../theme/global-styles'
import ClientLogo from '../../components/ClientLogo'
import PoweredByQITech from '../../components/PoweredByQITech'
import { ThemedButton } from '../../components/ThemedButton'
import darkOrLightMode from '../../utils/helpers/colorMode'
import { useThemeContext } from "../../contexts/ThemeContext";
import { getOrCreateBrowserKey } from '../../utils/fingerprint_utils'

export default function EnrichPersonalData({ setSignerStep, signData, setSignData, refreshSignData, appTheme }: {
    setSignerStep: (step: string) => void,
    signData: any,
    setSignData: (data: any) => void,
    refreshSignData: () => Promise<any>,
    appTheme: string | undefined
}) {

    const { clientThemeSettings } = useThemeContext();

    const [focusedOnce, setFocusedOnce] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState<'account' | 'pix_dict'>('pix_dict');
    const [pixDictType, setPixDictType] = useState<'cpf' | 'phone' | 'email' | 'random_key'>('cpf');
    const [pixDict, setPixDict] = useState<string>('');
    const [phone, setPhone] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [error, setError] = useState<boolean | any>(false)
    const [hasMadeRequest, setHasMadeRequest] = useState(false)

    const [hasClosedValidationScreen, setHasClosedValidationScreen] = useState(false);

    const additionalPersonalDataFields = signData.additional_personal_data_fields || []
    const signerAuthMethod = signData.signer_authentication_submission_method

    const personalDataConfiguration = useMemo(() => signData.personal_data || [
        {
            'type': 'personal_data',
        },
        {
            'type': 'address',
        },
        {
            'type': 'payment_data',
        }
    ], [signData.personal_data]);

    const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    const emailRegexCompat = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const BRAZIL_AREA_CODE_LENGTH = 2;
    const BRAZIL_MOBILE_NUMBER_LENGTH = 9;
    const BRAZIL_MOBILE_PHONE_TOTAL_DIGITS = BRAZIL_AREA_CODE_LENGTH + BRAZIL_MOBILE_NUMBER_LENGTH;

    function isValidEmail(email: string) {
        if (!emailRegexCompat.test(email)) {
            return false;
        }
        const [localPart, domainPart] = email.split('@');
        if (localPart.startsWith('.') || localPart.endsWith('.') || localPart.includes('..')) {
            return false;
        }
        const domainLabels = domainPart.split('.');
        for (let label of domainLabels) {
            if (label.startsWith('-') || label.endsWith('-') || label.length === 0) {
                return false;
            }
        }
        return true;
    }

    const [currentForm, setCurrentForm] = useState<'personal_data' | 'address' | 'payment_data'>('personal_data');

    useEffect(() => {
        if (personalDataConfiguration.length > 0) {
            setCurrentForm(personalDataConfiguration[0].type);
        }
    }, [personalDataConfiguration]);

    const handleFormIncrement = () => {

        const currentFormIndex = personalDataConfiguration.findIndex(form => form.type === currentForm);
        let nextForm = null;

        if (currentFormIndex === -1) {
            nextForm = personalDataConfiguration[0].type;
        } else if (currentFormIndex === personalDataConfiguration.length - 1) {
            nextForm = currentForm;
        } else {
            nextForm = personalDataConfiguration[currentFormIndex + 1].type;
        }

        setCurrentForm(nextForm);
    }

    useEffect(() => {
        if (signData.forced_payment_method === 'pix_document_number') {
            setPaymentMethod('pix_dict');
        }
    }, [signData]);

    const handleFormUpdate = (form: 'personal_data' | 'address' | 'payment_data') => {
        if (currentForm === 'address') {
            if (form === 'personal_data') {
                setCurrentForm('personal_data');
            }
        }
        if (currentForm === 'payment_data') {
            if (form === 'address') {
                setCurrentForm('address');
            } else if (form === 'personal_data') {
                setCurrentForm('personal_data');
            }
        }
    }

    const validateSubforms = () => {
        if (currentForm === 'personal_data') {
            if (!isValidEmail(email)) return false;
            if (!isValidBrazilMobilePhone(phone)) return false;

            if (additionalPersonalDataFields.length > 0) {
                if (maritalStatus.length === 0) return false
                if (placeOfBirth.length === 0) return false
            }
            return true;
        } else if (currentForm === 'payment_data') {
            if (paymentMethod === 'pix_dict') {
                if (pixDictType === 'phone') {
                    if (!isValidBrazilMobilePhone(pixDict)) return false;
                } else if (pixDictType === 'email') {
                    if (!isValidEmail(pixDict)) return false;
                } else if (pixDictType === 'random_key') {
                    if (!uuidRegex.test(pixDict)) return false;
                }
                return true
            }
            if (paymentMethod === 'account') {
                if (bankCode.length !== 3) return false;
                if (account.length < 7) return false;
                if (agency.length !== 4) return false;
                if (accountDigit.length !== 1) return false;
                return true;
            }
        } else if (currentForm === 'address') {
            if (address.street.length === 0) return false;
            if (address.number.length === 0) return false;
            if (address.neighborhood.length === 0) return false;
            if (address.city.length === 0) return false;
            if (address.uf.length === 0) return false;
            if (address.postal_code.length === 0) return false;
            return true;
        }
    }

    const [postalCodeSearched, setPostalCodeSearched] = useState<any>({})

    const [address, setAddress] = useState<any>(signData?.signer_data?.address ?? {
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        uf: 'SP',
        postal_code: '',
    });

    const [maritalStatus, setMaritalStatus] = useState<string>(additionalPersonalDataFields.length > 0 ? 'single' : '')
    const [placeOfBirth, setPlaceOfBirth] = useState<string>('')

    const [postalCodeLoading, setPostalCodeLoading] = useState(false)

    const validatePostalCode = (postalCode: string) => {
        const isValidFormat = /^\d{5}-\d{3}$/.test(postalCode);
        return isValidFormat
    }

    const [bankCode, setBankCode] = useState<string>('');
    const [agency, setAgency] = useState<string>('');
    const [account, setAccount] = useState<string>('');
    const [accountDigit, setAccountDigit] = useState<string>('');

    const [formValid, setFormValid] = useState(false);
    const [subformValid, setSubformValid] = useState(false);

    const sanitizePhoneDigits = (value: string): string => {
        return (value || '').replace(/\D/g, '').slice(0, BRAZIL_MOBILE_PHONE_TOTAL_DIGITS);
    }

    const isValidBrazilMobilePhone = (value: string): boolean => {
        return sanitizePhoneDigits(value).length === BRAZIL_MOBILE_PHONE_TOTAL_DIGITS;
    }

    const formatPhoneNumber = (value: string): string => {
        if (value) {
            const digits = sanitizePhoneDigits(value);
            return digits
                .replace(/(\d{2})(\d)/, '($1) $2')
                .replace(/(\d{5})(\d)/, '$1-$2')
                .slice(0, 15);
        }
        return '';
    }

    const formatCEP = (value: string): string => {
        if (value) {
            const digits = value.replace(/\D/g, '');
            return digits
                .replace(/(\d{5})(\d)/, '$1-$2')
                .slice(0, 9);
        }
        return '';
    }

    const formatAdressNumber = (value: string) => {
        if (value) {
            const digits = value.replace(/\D/g, '').slice(0, 5);
            return digits
        }
        return ''
    }

    const validate = () => {
        if (personalDataConfiguration.find(configuration => configuration.type === 'personal_data')) {
            if (!isValidEmail(email)) return false;
            if (!isValidBrazilMobilePhone(phone)) return false;
            
            if (additionalPersonalDataFields.length > 0){
                if(placeOfBirth.length === 0) return false
                if(maritalStatus.length === 0) return false
            }
                
        }
        if (personalDataConfiguration.find(configuration => configuration.type === 'payment_data')) {
            if (paymentMethod === 'pix_dict') {
                if (pixDictType === 'phone') {
                    if (!isValidBrazilMobilePhone(pixDict)) return false;
                } else if (pixDictType === 'email') {
                    if (!isValidEmail(pixDict)) return false;
                } else if (pixDictType === 'random_key') {
                    if (!uuidRegex.test(pixDict)) return false;
                }
            }
            if (paymentMethod === 'account') {
                if (bankCode.length !== 3) return false;
                if (account.length < 7) return false;
                if (agency.length !== 4) return false;
                if (accountDigit.length !== 1) return false;
            }
        }
        if (personalDataConfiguration.find(configuration => configuration.type === 'address')) {
            if (address.street.length === 0) return false;
            if (address.number.length === 0) return false;
            if (address.neighborhood.length === 0) return false;
            if (address.city.length === 0) return false;
            if (address.uf.length === 0) return false;
            if (address.postal_code.length === 0) return false;
        }
        return true;
    }

    useEffect(() => {
        const valid = validate();
        setFormValid(valid);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, phone, paymentMethod, pixDict, pixDictType, address, bankCode, agency, account, accountDigit, personalDataConfiguration, maritalStatus, placeOfBirth]);

    useEffect(() => {
        const valid = validateSubforms();
        setSubformValid(valid);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentForm, email, phone, paymentMethod, pixDict, pixDictType, address, bankCode, agency, account, accountDigit, personalDataConfiguration, maritalStatus, placeOfBirth]);

    const handleContinue = async () => {

        if (hasMadeRequest) {
            return
        }

        const formattedPixDict = pixDictType === 'phone' ? `+55${sanitizePhoneDigits(pixDict)}` : pixDict;

        const mutablePaymentData = paymentMethod === 'account' ? {
            account_data: {
                bank_code: bankCode,
                branch_number: agency,
                account_number: account,
                account_digit: accountDigit,
            }
        } : {
            pix_key: pixDictType === 'cpf' ? accountHolder : formattedPixDict,
        }

        const formattedPhone = sanitizePhoneDigits(phone)

        let browserKey = null;
        try {
            browserKey = await getOrCreateBrowserKey();
        } catch (err) {
        }

        const payload = {
            event_date: new Date().toISOString(),
            step: 'enrich_personal_data',
            phone: {
                international_dial_code: '55',
                area_code: formattedPhone.slice(0, 2),
                number: formattedPhone.slice(2),
            },
            email: email,
            ...mutablePaymentData,
            address: {
                street: address.street.trim(),
                number: address.number.trim(),
                complement: address.complement ? address.complement.trim() : undefined,
                neighborhood: address.neighborhood.trim(),
                city: address.city.trim(),
                uf: address.uf.trim(),
                postal_code: address.postal_code.trim(),
            },
            device_data: {
                device_key: browserKey
            }
        }

        if (maritalStatus.length > 0){
            payload['marital_status'] = maritalStatus
        }

        if (placeOfBirth.length > 0 ){
            payload['place_of_birth'] = placeOfBirth
        }

        const config = {
            headers:
            {
                'Authorization': signData.signer_token
            }
        }

        try {
            setLoading(true)
            setHasMadeRequest(true)
            const response = await axios.patch('/signer', payload, config);
            try {
                const refreshedSignData = await refreshSignData()
                setSignData({ ...signData, ...refreshedSignData, ...response.data })
                setSignerStep(response.data.next_step);
            } catch {
                setError(true)
            }

        } catch (error) {
            if ([401, 403, 409].includes((error.response || {}).status)) {
                setError({
                    "has_error": true,
                    "status": ((error.response || {}).status || 500)
                })
            } else {
                setError({
                    "has_error": true,
                    "status": 500
                })
            }
            setHasMadeRequest(false)
        }

        setLoading(false);

    }

    const handleContinueAddressConfirmation = async () => {
        if (!validateSubforms()) return;

        let browserKey = null;
        try {
            browserKey = await getOrCreateBrowserKey();
        } catch (err) { }

        const payload = {
            event_date: new Date().toISOString(),
            step: 'enrich_personal_data',
            address: {
                street: address.street.trim(),
                number: address.number.trim(),
                complement: address.complement ? address.complement.trim() : undefined,
                neighborhood: address.neighborhood.trim(),
                city: address.city.trim(),
                uf: address.uf.trim(),
                postal_code: address.postal_code.trim(),
            },
            device_data: {
                device_key: browserKey
            }
        }

        const config = {
            headers:
            {
                'Authorization': signData.signer_token
            }
        }

        try {
            setLoading(true)
            setHasMadeRequest(true)
            const response = await axios.patch('/signer', payload, config);
            try {
                const refreshedSignData = await refreshSignData()
                setSignData({ ...signData, ...refreshedSignData, ...response.data })
                setSignerStep(response.data.next_step);
            } catch {
                setError(true)
            }

        } catch (error) {
            if ([401, 403, 409].includes((error.response || {}).status)) {
                setError({
                    "has_error": true,
                    "status": ((error.response || {}).status || 500)
                })
            } else {
                setError({
                    "has_error": true,
                    "status": 500
                })
            }
            setHasMadeRequest(false)
        }

        setLoading(false);
    }

    const handlePostalCodeSearch = async (postalCode: string) => {
        setPostalCodeLoading(true)
        const config = {
            headers:
            {
                'Authorization': signData.signer_token
            }
        }

        try {
            const response = await axios.get(`/signer/postal_code?postal_code=${postalCode}`, config);
            const addressData = response.data
            const addressObject = {
                postal_code: postalCode,
                street: addressData.address ?? '',
                neighborhood: addressData.neighborhood ?? '',
                city: addressData.city ?? '',
                uf: addressData.short_state_name ?? ''
            }
            setPostalCodeSearched(addressObject)
            setAddress({ ...address, ...addressObject })
        } catch (error) {
            console.warn(error)
        } finally {
            setPostalCodeLoading(false)
        }
    }

    const isValidationScreenUp = useMemo(() => {
        return (
            !hasClosedValidationScreen
            && currentForm === 'address'
            && (personalDataConfiguration.find(configuration => configuration.type === currentForm)?.validate ?? false)
        );
    }, [hasClosedValidationScreen, currentForm, personalDataConfiguration]);

    if (!clientThemeSettings || !signData) {
        return <></>
    }

    if (loading) {
        return (
            <LoadingPage />
        )
    }

    if (error) {
        return (
            <ErrorPage
                error={error}
                redirectToLogin={() => setSignerStep("login")}
                resetError={() => setError({ has_error: false, status: 0 })}
                clientThemeSettings={clientThemeSettings}
            />
        )
    }

    const accountHolder = signData.signer_data.document_number;

    const translateType = (type: string) => {
        switch (type) {
            case 'personal_data':
                return 'Dados';
            case 'address':
                return 'Endereço';
            case 'payment_data':
                return 'Pagamento';
            default:
                return type;
        }
    }

    return (
        <G.Container style={{ padding: '24px 0' }}>
            <ClientLogo clientThemeSettings={clientThemeSettings} size="small" />
            <S.FormContainer>
                {personalDataConfiguration.length > 1 && <>
                    <G.Title>Insira seus dados</G.Title>
                    <S.Text>
                        Preencha os campos abaixo e depois clique em "continuar":
                    </S.Text>
                </>}
                <S.Divider />
                <S.ProgressIndicator>
                    {personalDataConfiguration.map((form, index) => (
                        <S.ProgressStep key={index}>
                            <S.ProgressStepNumber onClick={() => handleFormUpdate(form.type)} active={currentForm === form.type} clientTheme={clientThemeSettings} fontColorMode={darkOrLightMode(clientThemeSettings?.button_color)}>{index + 1}</S.ProgressStepNumber>
                            <S.ProgressStepLabel>{translateType(form.type)}</S.ProgressStepLabel>
                        </S.ProgressStep>
                    ))}
                </S.ProgressIndicator>
                <S.Divider />
                {currentForm === 'personal_data' &&
                    <>
                        <S.Subtitle>Informações de contato</S.Subtitle>
                        <S.InputContainer>
                            <FormField
                                hasError={focusedOnce.phone && !isValidBrazilMobilePhone(phone)}
                                labelValue={signerAuthMethod  === 'whatsapp' ? 'WhatsApp' : signerAuthMethod  === 'sms' ? 'SMS' : "Celular"}
                                fieldValue={formatPhoneNumber(phone)}
                                onChange={(e) => {
                                    setFocusedOnce({ ...focusedOnce, phone: true })
                                    setPhone(formatPhoneNumber(e.target.value).trim())
                                }}
                                errorMessage=""
                                type="numeric"
                            />
                            <FormField
                                hasError={focusedOnce.email && !isValidEmail(email)}
                                labelValue="Email"
                                fieldValue={email}
                                onChange={(e) => {
                                    setFocusedOnce({ ...focusedOnce, email: true })
                                    setEmail(e.target.value.toLowerCase().trim())
                                }}
                                errorMessage=""
                                type="email"
                            />
                            {
                                additionalPersonalDataFields.length > 0 && <S.Subtitle>Informações adicionais</S.Subtitle>
                            }

                            {
                                additionalPersonalDataFields.includes('marital_status') &&
                                <>
                                    <div style={{ color: "#7f8fa4", fontSize: "12px", fontWeight: "400", marginBottom: "-8px" }}>Estado Civil</div>

                                    <S.StyledSelect value={maritalStatus} onChange={(event) => {
                                        setMaritalStatus(event.target.value)
                                    }}>
                                        <option value="single">Solteiro</option>
                                        <option value="married">Casado</option>
                                        <option value="separated">Separado</option>
                                        <option value="divorced">Divorciado</option>
                                        <option value="widowed">Viúvo</option>
                                        <option value="other">Outros</option>
                                        <option value="legally_separated">Desquitado</option>
                                        <option value="cohabiting">Amasiado</option>
                                    </S.StyledSelect>
                                </>
                            }

                            {
                                additionalPersonalDataFields.includes('place_of_birth') && <FormField
                                    hasError={focusedOnce.placeOfBirth && placeOfBirth.length === 0}
                                    labelValue="Naturalidade (Informar Cidade - Estado)"
                                    fieldValue={placeOfBirth}
                                    onChange={(e) => {
                                        setFocusedOnce({ ...focusedOnce, placeOfBirth: true })
                                        setPlaceOfBirth(e.target.value)
                                    }}
                                    errorMessage=""
                                    type="text"
                                />
                            }

                        </S.InputContainer>
                    </>
                }
                {currentForm === 'address' ?
                    <>
                        {isValidationScreenUp && personalDataConfiguration.find(configuration => configuration.type === 'address')?.validate ? (
                            <>
                                <S.Subtitle>Confirme o endereço de entrega do cartão</S.Subtitle>
                                <S.Description>O cartão físico será enviado para o endereço abaixo. Certifique-se de que o endereço de entrega do cartão está correto, e que você conseguirá recebê-lo nesse local:</S.Description>
                                <S.InputContainer>
                                    <FormField labelValue="CEP" fieldValue={address.postal_code} type="numeric" disabled />
                                    <FormField labelValue="Cidade" fieldValue={address.city} type="text" disabled />
                                    <FormField labelValue="Estado" fieldValue={address.uf} type="text" disabled />
                                    <FormField labelValue="Bairro" fieldValue={address.neighborhood} type="text" disabled />
                                    <FormField labelValue="Logradouro" fieldValue={address.street} type="text" disabled />
                                    <FormField labelValue="Número" fieldValue={address.number} type="numeric" disabled />
                                    <FormField labelValue="Complemento" fieldValue={address.complement} type="text" disabled />
                                </S.InputContainer>
                                <S.ButtonContainer>
                                    <ThemedButton
                                        onClick={() => {
                                            setHasClosedValidationScreen(true);
                                        }}
                                        clientThemeSettings={clientThemeSettings}
                                        type='soft'
                                    >
                                        Alterar endereço
                                    </ThemedButton>
                                    <ThemedButton
                                        onClick={() => {
                                            setHasClosedValidationScreen(true);
                                            const isLast = personalDataConfiguration.findIndex(configuration => configuration.type === currentForm) === personalDataConfiguration.length - 1;

                                            if (!isLast) {
                                                handleFormIncrement();
                                            } else {
                                                if (personalDataConfiguration.length === 1 && currentForm === 'address') {
                                                    handleContinueAddressConfirmation();
                                                } else {
                                                    handleContinue();
                                                }
                                            }
                                        }}
                                        clientThemeSettings={clientThemeSettings}
                                    >
                                        Confirmar
                                    </ThemedButton>
                                </S.ButtonContainer>
                            </>
                        ) : (
                            <>
                                {personalDataConfiguration.find(configuration => configuration.type === 'address')?.validate ?
                                    <>
                                        <S.Subtitle>Atualize o endereço de entrega do cartão</S.Subtitle>
                                        <S.Description>O cartão físico será enviado para o endereço abaixo. Certifique-se de que o endereço de entrega do cartão está correto, e que você conseguirá recebê-lo nesse local:</S.Description>
                                    </>
                                    :
                                    <S.Subtitle>Informações de endereço</S.Subtitle>
                                }
                                <S.InputContainer>
                                    <FormField
                                        hasError={focusedOnce.addressPostalCode && address.postal_code.length === 0}
                                        labelValue="CEP"
                                        fieldValue={address.postal_code}
                                        onChange={(e) => {
                                            setFocusedOnce({ ...focusedOnce, addressPostalCode: true })
                                            setAddress({ ...address, postal_code: formatCEP(e.target.value) })
                                            if (validatePostalCode(formatCEP(e.target.value))) {
                                                if (!postalCodeLoading) {
                                                    handlePostalCodeSearch(formatCEP(e.target.value))
                                                }
                                            } else {
                                                setPostalCodeSearched({})
                                            }
                                        }}
                                        errorMessage=""
                                        type="numeric"
                                    />
                                    <FormField
                                        hasError={focusedOnce.addressCity && address.city.length === 0}
                                        labelValue="Cidade"
                                        fieldValue={address.city}
                                        onChange={(e) => {
                                            setFocusedOnce({ ...focusedOnce, addressCity: true })
                                            setAddress({ ...address, city: e.target.value })
                                        }}
                                        disabled={postalCodeSearched?.city}
                                        errorMessage=""
                                        type="text"
                                    />
                                    <div style={{ color: "#7f8fa4", fontSize: "12px", fontWeight: "400", marginBottom: "-8px" }}>Estado</div>

                                    <S.StyledSelect disabled={postalCodeSearched.uf} value={address.uf} onChange={(event) => {
                                        setAddress({ ...address, uf: event.target.value })
                                    }}>
                                        <option value="SP">São Paulo (SP)</option>
                                        <option value="AC">Acre (AC)</option>
                                        <option value="AL">Alagoas (AL)</option>
                                        <option value="AM">Amazonas (AM)</option>
                                        <option value="AP">Amapá (AP)</option>
                                        <option value="BA">Bahia (BA)</option>
                                        <option value="CE">Ceará (CE)</option>
                                        <option value="DF">Distrito Federal (DF)</option>
                                        <option value="ES">Espírito Santo (ES)</option>
                                        <option value="GO">Goiás (GO)</option>
                                        <option value="MA">Maranhão (MA)</option>
                                        <option value="MG">Minas Gerais (MG)</option>
                                        <option value="MS">Mato Grosso do Sul (MS)</option>
                                        <option value="MT">Mato Grosso (MT)</option>
                                        <option value="PA">Pará (PA)</option>
                                        <option value="PB">Paraíba (PB)</option>
                                        <option value="PE">Pernambuco (PE)</option>
                                        <option value="PI">Piauí (PI)</option>
                                        <option value="PR">Paraná (PR)</option>
                                        <option value="RJ">Rio de Janeiro (RJ)</option>
                                        <option value="RN">Rio Grande do Norte (RN)</option>
                                        <option value="RO">Rondônia (RO)</option>
                                        <option value="RR">Roraima (RR)</option>
                                        <option value="RS">Rio Grande do Sul (RS)</option>
                                        <option value="SC">Santa Catarina (SC)</option>
                                        <option value="SE">Sergipe (SE)</option>
                                        <option value="TO">Tocantins (TO)</option>
                                    </S.StyledSelect>
                                    <FormField
                                        hasError={focusedOnce.addressNeighborhood && address.neighborhood.length === 0}
                                        labelValue="Bairro"
                                        fieldValue={address.neighborhood}
                                        onChange={(e) => {
                                            setFocusedOnce({ ...focusedOnce, addressNeighborhood: true })
                                            setAddress({ ...address, neighborhood: e.target.value })
                                        }}
                                        disabled={postalCodeSearched?.neighborhood}
                                        errorMessage=""
                                        type="text"
                                    />
                                    <FormField
                                        hasError={focusedOnce.addressStreet && address.street.length === 0}
                                        labelValue="Logradouro"
                                        fieldValue={address.street}
                                        onChange={(e) => {
                                            setFocusedOnce({ ...focusedOnce, addressStreet: true })
                                            setAddress({ ...address, street: e.target.value })
                                        }}
                                        disabled={postalCodeSearched?.street}
                                        errorMessage=""
                                        type="text"
                                    />
                                    <FormField
                                        hasError={focusedOnce.addressNumber && (address.number.length === 0 || address.number.length > 5)}
                                        labelValue="Número"
                                        fieldValue={address.number}
                                        onChange={(e) => {
                                            setFocusedOnce({ ...focusedOnce, addressNumber: true })
                                            setAddress({ ...address, number: formatAdressNumber(e.target.value) })
                                        }}
                                        errorMessage=""
                                        type="numeric"
                                    />
                                    <FormField
                                        hasError={focusedOnce.addressComplement && address.complement.length === 0}
                                        labelValue="Complemento"
                                        fieldValue={address.complement}
                                        onChange={(e) => {
                                            setFocusedOnce({ ...focusedOnce, addressComplement: true })
                                            setAddress({ ...address, complement: e.target.value })
                                        }}
                                        errorMessage=""
                                        type="text"
                                    />
                                </S.InputContainer>
                            </>
                        )
                        }
                    </>
                    : null
                }
                {currentForm === 'payment_data' &&
                    <>
                        <S.Subtitle>Informações de pagamento</S.Subtitle>
                        {signData.forced_payment_method === 'pix_document_number' ?
                            <S.Wrapper>
                                <FormField
                                    hasError={false}
                                    labelValue="Chave PIX (CPF)"
                                    fieldValue={signData.signer_data.document_number}
                                    disabled
                                    errorMessage=""
                                    readOnly
                                />
                                <S.Callout clientTheme={clientThemeSettings}>
                                    <S.CalloutTitle>Atenção</S.CalloutTitle>
                                    <S.CalloutText>
                                        Lembre-se de verificar se você tem uma chave PIX cadastrada no seu CPF.
                                    </S.CalloutText>
                                </S.Callout>
                            </S.Wrapper>
                            :
                            <>
                                <S.Selector>
                                    <S.SelectorOption selected={paymentMethod === 'pix_dict'} onClick={() => {
                                        setPaymentMethod('pix_dict');
                                    }} clientTheme={clientThemeSettings} fontColorMode={darkOrLightMode(clientThemeSettings?.background_color)}>Chave PIX</S.SelectorOption>
                                    <S.SelectorOption selected={paymentMethod === 'account'} onClick={() => {
                                        setPaymentMethod('account');
                                    }} clientTheme={clientThemeSettings} fontColorMode={darkOrLightMode(clientThemeSettings?.background_color)}>Conta bancária</S.SelectorOption>
                                </S.Selector>
                                {paymentMethod === 'pix_dict' &&
                                    <>
                                        <S.Description><b>Tipo de chave PIX</b></S.Description>
                                        <S.RadioGroup>
                                            <S.RadioRoot>
                                                <S.Radio type='radio' checked={pixDictType === 'cpf'} onChange={() => {
                                                    setPixDictType('cpf');
                                                    setPixDict('');
                                                }} />
                                                <S.RadioLabel>CPF</S.RadioLabel>
                                            </S.RadioRoot>
                                            <S.RadioRoot>
                                                <S.Radio type='radio' checked={pixDictType === 'phone'} onChange={() => {
                                                    setPixDictType('phone');
                                                    setPixDict('');
                                                }} />
                                                <S.RadioLabel>Telefone</S.RadioLabel>
                                            </S.RadioRoot>
                                            <S.RadioRoot>
                                                <S.Radio type='radio' checked={pixDictType === 'email'} onChange={() => {
                                                    setPixDictType('email');
                                                    setPixDict('');
                                                }} />
                                                <S.RadioLabel>Email</S.RadioLabel>
                                            </S.RadioRoot>
                                            <S.RadioRoot>
                                                <S.Radio type='radio' checked={pixDictType === 'random_key'} onChange={() => {
                                                    setPixDictType('random_key');
                                                    setPixDict('');
                                                }} />
                                                <S.RadioLabel>Chave aleatória</S.RadioLabel>
                                            </S.RadioRoot>
                                        </S.RadioGroup>
                                        <S.InputContainer>
                                            {pixDictType === 'cpf' &&
                                                <>
                                                    <FormField
                                                        hasError={false}
                                                        labelValue="Chave PIX"
                                                        fieldValue={signData.signer_data.document_number}
                                                        disabled
                                                        errorMessage=""
                                                    />
                                                    <S.Callout clientTheme={clientThemeSettings}>
                                                        <S.CalloutTitle>Atenção</S.CalloutTitle>
                                                        <S.CalloutText>
                                                            Lembre-se de verificar se você tem uma chave PIX cadastrada no seu CPF.
                                                        </S.CalloutText>
                                                    </S.Callout>
                                                </>
                                            }
                                            {pixDictType === 'phone' &&
                                                <FormField
                                                    hasError={focusedOnce.pixDictPhone && !isValidBrazilMobilePhone(pixDict)}
                                                    labelValue="Chave PIX"
                                                    fieldValue={formatPhoneNumber(pixDict)}
                                                    onChange={(e) => {
                                                        setFocusedOnce({ ...focusedOnce, pixDictPhone: true })
                                                        setPixDict(formatPhoneNumber(e.target.value.trim()))
                                                    }}
                                                    errorMessage=""
                                                    type="numeric"
                                                />
                                            }
                                            {pixDictType === 'email' &&
                                                <FormField
                                                    hasError={focusedOnce.pixDictEmail && !isValidEmail(pixDict)}
                                                    labelValue="Chave PIX"
                                                    fieldValue={pixDict}
                                                    onChange={(e) => {
                                                        setFocusedOnce({ ...focusedOnce, pixDictEmail: true })
                                                        setPixDict(e.target.value.toLowerCase().trim())
                                                    }}
                                                    errorMessage=""
                                                    type="email"
                                                />
                                            }
                                            {pixDictType === 'random_key' &&
                                                <FormField
                                                    hasError={focusedOnce.pixDictRandomKey && !uuidRegex.test(pixDict)}
                                                    labelValue="Chave PIX"
                                                    fieldValue={pixDict}
                                                    onChange={(e) => {
                                                        setFocusedOnce({ ...focusedOnce, pixDictRandomKey: true })
                                                        setPixDict(e.target.value.toLowerCase().trim())
                                                    }}
                                                    errorMessage=""
                                                />
                                            }
                                        </S.InputContainer>
                                    </>
                                }
                                {paymentMethod === 'account' &&
                                    <S.InputContainer>
                                        <FormField
                                            hasError={focusedOnce.bankCode && bankCode.length !== 3}
                                            labelValue="Código do banco"
                                            fieldValue={bankCode}
                                            onChange={(e) => {
                                                setFocusedOnce({ ...focusedOnce, bankCode: true })
                                                if (e.target.value.length <= 3) {
                                                    setBankCode(e.target.value.trim())
                                                }
                                            }}
                                            errorMessage=""
                                            type="numeric"
                                        />
                                        <FormField
                                            hasError={focusedOnce.agency && agency.length !== 4}
                                            labelValue="Agência"
                                            fieldValue={agency}
                                            onChange={(e) => {
                                                setFocusedOnce({ ...focusedOnce, agency: true })
                                                if (e.target.value.length <= 4) {
                                                    setAgency(e.target.value.trim())
                                                }
                                            }}
                                            errorMessage=""
                                            type="numeric"
                                        />
                                        <FormField
                                            hasError={focusedOnce.account && account.length < 7}
                                            labelValue="Conta"
                                            fieldValue={account}
                                            onChange={(e) => {
                                                setFocusedOnce({ ...focusedOnce, account: true })
                                                if (e.target.value.length <= 8) {
                                                    setAccount(e.target.value.trim())
                                                }
                                            }}
                                            errorMessage=""
                                            type="numeric"
                                        />
                                        <FormField
                                            hasError={focusedOnce.accountDigit && accountDigit.length !== 1}
                                            labelValue="Dígito da conta"
                                            fieldValue={accountDigit}
                                            onChange={(e) => {
                                                setFocusedOnce({ ...focusedOnce, accountDigit: true })
                                                if (e.target.value.length <= 1) {
                                                    setAccountDigit(e.target.value.trim())
                                                }
                                            }}
                                            errorMessage=""
                                            type="numeric"
                                        />
                                        <FormField
                                            hasError={false}
                                            labelValue="CPF do titular"
                                            fieldValue={accountHolder}
                                            disabled
                                            errorMessage=""
                                        />
                                    </S.InputContainer>

                                }
                            </>
                        }
                    </>
                }
            </S.FormContainer>

            <S.Footer>
                {!isValidationScreenUp &&
                    <>
                        {personalDataConfiguration.findIndex(configuration => configuration.type === currentForm) === personalDataConfiguration.length - 1 ?
                            <ThemedButton
                                onClick={() => {
                                    if (formValid) {
                                        if (personalDataConfiguration.length === 1 && currentForm === 'address') {
                                            handleContinueAddressConfirmation()
                                        } else {
                                            handleContinue()
                                        }
                                    }
                                }}
                                type='primary'
                                clientThemeSettings={clientThemeSettings}
                                $disabled={!formValid}
                            >
                                Continuar
                            </ThemedButton>
                            :
                            <ThemedButton
                                onClick={() => {
                                    if (subformValid) {
                                        handleFormIncrement();
                                    }
                                }}
                                type='primary'
                                clientThemeSettings={clientThemeSettings}
                                $disabled={!subformValid}
                            >
                                Continuar
                            </ThemedButton>
                        }
                    </>
                }
                <PoweredByQITech />
            </S.Footer>
        </G.Container>
    );

};